import type { CharacterConfig, CharacterId } from "./types";

/**
 * Character Factory - manages character definitions and retrieval
 *
 * Usage:
 *   CharacterFactory.register(warriorConfig);
 *   const warrior = CharacterFactory.get("warrior");
 *   const allChars = CharacterFactory.getAll();
 */
export class CharacterFactory {
  private static registry = new Map<CharacterId, CharacterConfig>();

  /**
   * Register a character configuration
   * @param config - Character configuration to register
   * @throws Error if character with this ID is already registered
   */
  static register(config: CharacterConfig): void {
    if (this.registry.has(config.id)) {
      throw new Error(`Character with id "${config.id}" is already registered`);
    }
    this.registry.set(config.id, config);
  }

  /**
   * Get a character configuration by ID
   * @param id - Character ID to retrieve
   * @returns Character configuration
   * @throws Error if character ID is not found
   */
  static get(id: CharacterId): CharacterConfig {
    const config = this.registry.get(id);
    if (!config) {
      throw new Error(`Character with id "${id}" not found. Did you forget to register it?`);
    }
    return config;
  }

  /**
   * Get all registered character configurations
   * @returns Array of all character configurations
   */
  static getAll(): CharacterConfig[] {
    return Array.from(this.registry.values());
  }

  /**
   * Check if a character is registered
   * @param id - Character ID to check
   * @returns True if character is registered
   */
  static has(id: CharacterId): boolean {
    return this.registry.has(id);
  }

  /**
   * Clear all registered characters (primarily for testing)
   */
  static clear(): void {
    this.registry.clear();
  }
}
